import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const sectionStyle = { marginBottom: 28 };
  const h2Style = { fontSize: 20, fontWeight: 800, marginBottom: 10, color: "#1a1a1a" };
  const pStyle = { fontSize: 15, lineHeight: 1.7, color: "#444", marginBottom: 10 };
  const ulStyle = { paddingLeft: 24, marginBottom: 10 };
  const liStyle = { fontSize: 15, lineHeight: 1.7, color: "#444", marginBottom: 6 };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ marginBottom: 30 }}>
        <Link to="/" style={{ color: "#dc2626", textDecoration: "none", fontWeight: 700, fontSize: 20 }}>
          RoadOptix
        </Link>
      </div>

      <h1 style={{ fontSize: 32, fontWeight: 900, marginBottom: 8, color: "#111" }}>Privacy Policy</h1>
      <p style={{ fontSize: 14, color: "#888", marginBottom: 30 }}>Last Updated: February 26, 2026</p>

      <div style={sectionStyle}>
        <h2 style={h2Style}>1. Introduction</h2>
        <p style={pStyle}>
          Symple Code Business Systems, LLC ("Company", "we", "us", or "our") operates the RoadOptix 
          transportation management platform ("Service") accessible at roadoptix.com and demo.roadoptix.com. 
          This Privacy Policy describes how we collect, use, disclose, and protect your personal information 
          when you use our Service.
        </p>
        <p style={pStyle}>
          By using our Service, you agree to the collection and use of information in accordance with this policy.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>2. Information We Collect</h2>
        <p style={pStyle}><strong>Account Information:</strong></p>
        <ul style={ulStyle}>
          <li style={liStyle}>Name, email address, and phone number</li>
          <li style={liStyle}>Company name, MC number, DOT number</li>
          <li style={liStyle}>Business address and contact information</li>
          <li style={liStyle}>Login credentials (passwords are encrypted)</li>
        </ul>
        <p style={pStyle}><strong>Operational Data:</strong></p>
        <ul style={ulStyle}>
          <li style={liStyle}>Load and shipment information (origin, destination, rates, weights)</li>
          <li style={liStyle}>Bid and tender history</li>
          <li style={liStyle}>GPS location data (when carrier tracking is enabled)</li>
          <li style={liStyle}>Documents uploaded (BOL, POD, insurance certificates)</li>
          <li style={liStyle}>FMCSA compliance and safety data</li>
        </ul>
        <p style={pStyle}><strong>Communication Data:</strong></p>
        <ul style={ulStyle}>
          <li style={liStyle}>SMS messages sent through our platform</li>
          <li style={liStyle}>Phone numbers used for SMS notifications</li>
          <li style={liStyle}>Email communications related to the Service</li>
        </ul>
        <p style={pStyle}><strong>Technical Data:</strong></p>
        <ul style={ulStyle}>
          <li style={liStyle}>IP address, browser type, device information</li>
          <li style={liStyle}>Usage logs and analytics</li>
          <li style={liStyle}>Cookies and similar tracking technologies</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>3. How We Use Your Information</h2>
        <ul style={ulStyle}>
          <li style={liStyle}>To provide and maintain the Service, including load matching, bidding, and dispatching</li>
          <li style={liStyle}>To send SMS notifications about load alerts, backhaul matches, status updates, and account activity</li>
          <li style={liStyle}>To verify carrier compliance through FMCSA data</li>
          <li style={liStyle}>To process payments and factoring requests</li>
          <li style={liStyle}>To provide GPS tracking for active shipments</li>
          <li style={liStyle}>To improve and personalize the Service</li>
          <li style={liStyle}>To communicate with you about your account and Service updates</li>
          <li style={liStyle}>To comply with legal obligations</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>4. SMS/Text Messaging</h2>
        <p style={pStyle}>
          We use SMS messaging to send you important notifications related to our Service, including:
        </p>
        <ul style={ulStyle}>
          <li style={liStyle}>AI-powered backhaul load match alerts</li>
          <li style={liStyle}>Load status updates (pickup, delivery, etc.)</li>
          <li style={liStyle}>Bid notifications and tender alerts</li>
          <li style={liStyle}>Account and security notifications</li>
        </ul>
        <p style={pStyle}>
          <strong>Message frequency varies</strong> based on your activity and load availability. 
          Message and data rates may apply. You can opt out at any time by replying STOP to any message 
          or updating your notification preferences in your carrier profile. 
          See our <Link to="/sms-consent" style={{ color: "#dc2626" }}>SMS Terms</Link> for full details.
        </p>
        <p style={pStyle}>
          <strong>We do not sell, rent, or share your phone number or SMS opt-in data with third parties 
          for their marketing purposes.</strong> Phone numbers collected for SMS are used solely to deliver 
          Service-related notifications.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>5. Data Sharing</h2>
        <p style={pStyle}>We may share your information with:</p>
        <ul style={ulStyle}>
          <li style={liStyle}><strong>Other platform users:</strong> Carriers see broker/shipper info on loads; brokers see carrier bids and compliance data</li>
          <li style={liStyle}><strong>Service providers:</strong> Twilio (SMS delivery), payment processors, mapping services</li>
          <li style={liStyle}><strong>FMCSA and regulatory bodies:</strong> When required for compliance verification</li>
          <li style={liStyle}><strong>Legal requirements:</strong> When required by law, subpoena, or legal process</li>
        </ul>
        <p style={pStyle}>
          We do not sell your personal information to third parties for marketing purposes.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>6. Data Security</h2>
        <p style={pStyle}>
          We implement industry-standard security measures including encrypted data transmission (TLS/SSL), 
          encrypted password storage (bcrypt), secure database access controls, and regular security audits. 
          However, no method of electronic transmission or storage is 100% secure.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>7. Data Retention</h2>
        <p style={pStyle}>
          We retain your data for as long as your account is active or as needed to provide the Service. 
          Shipment records may be retained for up to 7 years for regulatory compliance. You may request 
          deletion of your account by contacting us.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>8. Your Rights</h2>
        <p style={pStyle}>You have the right to:</p>
        <ul style={ulStyle}>
          <li style={liStyle}>Access and receive a copy of your personal data</li>
          <li style={liStyle}>Correct inaccurate personal data</li>
          <li style={liStyle}>Request deletion of your personal data</li>
          <li style={liStyle}>Opt out of SMS notifications at any time</li>
          <li style={liStyle}>Withdraw consent for data processing</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>9. Children's Privacy</h2>
        <p style={pStyle}>
          Our Service is not intended for individuals under the age of 18. We do not knowingly collect 
          personal information from children.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>10. Changes to This Policy</h2>
        <p style={pStyle}>
          We may update this Privacy Policy from time to time. We will notify you of material changes 
          by posting the updated policy on this page and updating the "Last Updated" date.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>11. Contact Us</h2>
        <p style={pStyle}>
          If you have questions about this Privacy Policy, contact us at:
        </p>
        <p style={pStyle}>
          <strong>Symple Code Business Systems, LLC</strong><br />
          Houston, TX<br />
          Email: privacy@roadoptix.com<br />
          Phone: (346) 224-0790
        </p>
      </div>

      <div style={{ borderTop: "1px solid #eee", paddingTop: 20, marginTop: 40, fontSize: 13, color: "#888" }}>
        <Link to="/terms" style={{ color: "#888", marginRight: 20 }}>Terms of Service</Link>
        <Link to="/sms-consent" style={{ color: "#888", marginRight: 20 }}>SMS Terms</Link>
        <Link to="/privacy" style={{ color: "#888" }}>Privacy Policy</Link>
      </div>
    </div>
  );
}
